import {
  Enums_DocumentType,
  Requirements_Documents,
  Requirements_InsertDocument,
} from "data/endpoints";
import IRepository from "data/IRepository";
import { getRouteParams } from "features/shared/helpers/routeParams";
import { useInjection } from "infrastructure/ioc/ioc.react";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import React, { useState } from "react";
import DocumentSimplifiedDTO from "features/invoices/dtos/DocumentSimplifiedDTO";
import PagedDataResultDTO from "features/shared/dtos/PagedDataResultDTO";
import PageCard from "features/shared/components/card/PageCard";
import RequirementDocument from "../components/RequirementDocument";
import {
  getPaginationDefaults,
  getQueryParams,
} from "features/shared/helpers/queryParams";
import Pagination from "features/shared/components/pagination/Pagination";
import InsertRequirementDocumentDialog from "../components/insertDocumentDialog/InsertRequirementDocumentDialog";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { RequirementDetail_InsertRequirementDocumentDialog } from "features/shared/components/loaders/promiseLoadingAreas";
import { trackPromise } from "react-promise-tracker";
import { UserEnumDTO } from "features/shared/enums/dynamic/UserEnumDTO";
import DocumentTypeEnumDTO from "features/shared/enums/dynamic/DocumentTypeEnumDTO";
import DataQuery from "data/DataQuery";
import { useQuery } from "data/hooks/useQuery";
import NoContentWrapper from "features/shared/components/noContentWrapper/NoContentWrapper";
import RolesChecker from "features/customer/components/RolesChecker";
import CardHeaderButton from "features/shared/components/card/CardHeaderButton";
import { useTranslation } from "react-i18next";

const pageSize = 5;

interface Props {
  requirementId: string;
  requirementType: UserEnumDTO;
  addedDocumentsCount: number;
  setAddedDocumentsCount: React.Dispatch<React.SetStateAction<number>>;
}

const RequirementDocumentsSection = ({
  requirementId,
  requirementType,
  addedDocumentsCount,
  setAddedDocumentsCount
}: Props) => {
  // translations
  const tLabels = useTranslation("requirements\\requirements").t;

  // DI
  const documentsRepo = useInjection<IRepository<DocumentSimplifiedDTO>>(
    IocTypes.Repository
  );
  const documentsTypeRepo = useInjection<IRepository<DocumentTypeEnumDTO>>(
    IocTypes.Repository
  );

  // state
  const [queryParams, setQueryParams] = useState<URLSearchParams>(
    getPaginationDefaults(pageSize)
  );
  const [documentDialogShow, setDocumentDialogShow] = useState(false);
  const [documentTypes, setDocumentTypes] = useState<DocumentTypeEnumDTO[]>([]);

  // side effects
  // fetch documents
  const query: DataQuery = {
    url: Requirements_Documents,
    routeParams: getRouteParams("requirementId", requirementId),
    params: queryParams,
  };
  const { data: documents, loadStatus } = useQuery<PagedDataResultDTO<DocumentSimplifiedDTO>>(
    query,
    { count: 0, result: [] },
    [requirementId, queryParams, addedDocumentsCount],
    { useTracker: false }
  );

  // handlers
  const paginationChangePageHandler = (pageNumber: number) => {
    let paramas = new URLSearchParams(queryParams);
    paramas.set("Page", pageNumber.toString());
    setQueryParams(paramas);
  };

  const openInsertDocumentDialogHandler = async () => {
    if (documentTypes.length == 0) {
      let result = await trackPromise(
        documentsTypeRepo.get({
          url: Enums_DocumentType,
          params: getQueryParams("RequirementType", requirementType.code!),
        })
      );

      setDocumentTypes(result);
    }

    setDocumentDialogShow(true);
  };

  const insertRequirementDocumentHandler = async (
    document: DocumentSimplifiedDTO,
    files: FileList
  ) => {
    let response = await trackPromise(
      documentsRepo.postWithFile({
        url: Requirements_InsertDocument,
        files: files,
        filesKey: "File",
        object: document,
        objectKey: "Document",
        routeParams: getRouteParams("requirementId", requirementId),
      }),
      RequirementDetail_InsertRequirementDocumentDialog
    );

    if (response.status < 300) {
      setAddedDocumentsCount(addedDocumentsCount + 1);
    }

    setDocumentDialogShow(false);
  };

  // sub-renders
  const renderDocuments = () => {
    return (
      <NoContentWrapper
        data={documents}
        loadStatus={loadStatus}
        noContentRender={() => <div className="text-muted">{tLabels("requirementDocumentSection.documentsNotFound")}</div>}
      >
        <>
          {documents.result.map((item, index) => (
            <div className="py-1" key={index}>
              <RequirementDocument document={item} />
            </div>
          ))}
          {renderPagination()}
        </>
      </NoContentWrapper>
    );
  };

  const renderPagination = () => {
    if (documents.count > pageSize) {
      return (
        <>
          <hr />
          <div className="d-flex justify-content-center pb-1">
            <Pagination
              urlParams={queryParams}
              count={documents.count}
              pageSize={pageSize}
              onPageChange={paginationChangePageHandler}
              useXsView
            />
          </div>
        </>
      );
    }
  }

  const renderActionBtns = () => {
    let result: JSX.Element[] = [];
    let elem = (
      <RolesChecker enabledRoles="Contributor">
        <CardHeaderButton
          type="button"
          title={tLabels("requirementDocumentSection.addDocument")}
          onClick={openInsertDocumentDialogHandler}
          key={requirementId}
        >
          <FontAwesomeIcon icon="plus" />
        </CardHeaderButton>
      </RolesChecker>
    );

    result.push(elem);
    return result;
  };

  // render
  return (
    <PageCard title={tLabels("requirementDocumentSection.cardTitle")} headerActionBtns={renderActionBtns}>
      {renderDocuments()}
      <InsertRequirementDocumentDialog
        show={documentDialogShow}
        setShow={setDocumentDialogShow}
        onSubmit={insertRequirementDocumentHandler}
        documentTypes={documentTypes}
      />
    </PageCard>
  );
};

export default RequirementDocumentsSection;
